import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { Avatar, EmptyState, Loader } from "@threadws/jute";
import type { CalendarEvent } from "../api/generated";
import { calendarEventsListOptions, meMeOptions, usersListOptions } from "../api/generated/@tanstack/react-query.gen";
import { dayRange, formatTime, parseDay, today, type Day } from "../utils/calendarDay";
import { userName } from "../utils/userName";
import { useNow } from "../utils/useNow";
import { DayCalendar } from "./home/DayCalendar";
import { TaskCapture } from "./home/TaskCapture";
import { TaskList } from "./home/TaskList";

const partOfDay = (now: Date) => (now.getHours() < 12 ? "morning" : now.getHours() < 17 ? "afternoon" : "evening");

/** "4 events today, 2 still to come." All-day events count, but are never "to come". */
function summarise(events: CalendarEvent[] | undefined, day: Day, now: Date): string {
  if (!events) return "Looking at your day…";
  const count = `${events.length} event${events.length === 1 ? "" : "s"}`;
  if (day !== today()) return events.length === 0 ? "Nothing in your calendar that day." : `${count} that day.`;
  if (events.length === 0) return "Nothing in your calendar today.";
  const left = events.filter((event) => !event.allDay && new Date(event.endsAt) > now).length;
  return left === 0 ? `${count} today, all done.` : `${count} today, ${left} still to come.`;
}

/**
 * Today: a greeting, a line about the day, your tasks, and the day's calendar across your personas.
 * `?date=` shows another day's calendar. Tasks sit beside the calendar on wide screens, above it otherwise.
 */
export function HomePage() {
  const now = useNow();
  const [, navigate] = useLocation();
  const day = parseDay(new URLSearchParams(useSearch()).get("date"));
  const range = dayRange(day);
  const { data: me } = useQuery(meMeOptions());
  const { data: users } = useQuery(usersListOptions());
  const mine = me && users ? (users.find((user) => user.id === me.id)?.personaIds ?? []) : undefined;
  // Your personas; everyone's until you have some.
  const { data, isPending, isPlaceholderData, error } = useQuery({
    ...calendarEventsListOptions({ query: { ...range, ...(mine?.length ? { personaIds: mine } : {}) } }),
    enabled: mine !== undefined,
    placeholderData: keepPreviousData,
  });
  const greeting = `Good ${partOfDay(now)}${me?.firstName ? `, ${me.firstName}` : ""}`;
  const goTo = (next: Day) => navigate(next === today() ? "/" : `/?date=${next}`);

  return (
    <section className="flex w-full flex-col gap-5 px-7">
      <header className="flex flex-wrap items-end justify-between gap-5">
        <div className="flex flex-col gap-2">
          <h1 className="flex items-center gap-1.5 font-babyblue text-pixel-1 text-accent-fg">
            {greeting}
            <span aria-hidden className="h-4 w-2 bg-accent-fg motion-safe:animate-blink" />
          </h1>
          <p className="text-sm text-pretty text-text-secondary">{summarise(data?.events, day, now)}</p>
        </div>
        <div className="flex items-center gap-3.5">
          <time
            dateTime={now.toISOString()}
            className="rounded-sm border border-border-subtle px-2.5 py-1.5 font-babyblue text-pixel-1 text-text-primary"
          >
            {formatTime(now.toISOString())}
          </time>
          {me && <Avatar name={userName(me)} size="md" />}
        </div>
      </header>
      {mine?.length === 0 && (
        <p className="text-sm text-text-secondary">
          Showing everyone's personas, as none are yours yet. Tick your name under People on a persona to make it yours.
        </p>
      )}
      <div className="grid grid-cols-1 items-start gap-7 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <aside className="flex min-w-0 flex-col gap-5 xl:order-2">
          <TaskCapture />
          <TaskList personaIds={mine} />
        </aside>
        {error ? (
          <EmptyState title="Couldn't load the calendar" description={error.message} />
        ) : isPending ? (
          <Loader label="Loading the day" />
        ) : data.calendars.length === 0 ? (
          <EmptyState
            title="No calendars yet"
            description="Connect a Google account to a persona to see its calendars here."
            actionLabel="Go to personas"
            onAction={() => navigate("/personas")}
          />
        ) : (
          <div className={`min-w-0 ${isPlaceholderData ? "opacity-60 transition-opacity" : "transition-opacity"}`}>
            <DayCalendar day={day} range={range} data={data} onDayChange={goTo} />
          </div>
        )}
      </div>
    </section>
  );
}
